import React, { useCallback, useEffect, useState } from 'react';
import Button from '../components/Button';
import { PaymentProvider } from '../services/orderService';
import {
    organizerSettlementsService,
    Settlement,
    SettlementStatus,
    SettlementSummary
} from '../services/organizerSettlementsService';
import { DATE_TIME_SHORT, formatEventDate, formatInstant } from '../utils/dates';

const PROVIDER_LABELS: Record<PaymentProvider, string> = {
    BOLD: 'Bold',
    WOMPI: 'Wompi',
    MERCADOPAGO: 'Mercado Pago'
};

const STATUS: Record<SettlementStatus, { label: string; badge: string }> = {
    OPEN: { label: 'En curso', badge: 'st-badge-info' },
    ISSUED: { label: 'Emitida', badge: 'st-badge-warning' },
    VOID: { label: 'Anulada', badge: 'st-badge-neutral' }
};

const formatCurrency = (amount: number, currency = 'COP') =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);

// Net and VAT have cents
const formatExact = (amount: number, currency = 'COP') =>
    new Intl.NumberFormat('es-CO', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);

const formatNumber = (value: number) => new Intl.NumberFormat('es-CO').format(value);

const formatPercent = (rate: number) =>
    `${new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 }).format(rate * 100)} %`;

const Figure = ({ label, children, strong }: { label: string; children: React.ReactNode; strong?: boolean }) => (
    <div>
        <p className="text-xs text-[var(--st-text-muted)] uppercase font-bold tracking-wider mb-1">{label}</p>
        <p className={`text-[var(--st-text-primary)] ${strong ? 'font-display text-xl font-bold' : 'text-sm'}`}>{children}</p>
    </div>
);

// When the statement is (or will be) issued
const cutOffText = (summary: SettlementSummary) => {
    if (summary.status === 'VOID') return 'Evento cancelado: no genera cargo.';
    if (summary.status === 'ISSUED' && summary.issuedAt) return `Emitida el ${formatInstant(summary.issuedAt, DATE_TIME_SHORT)}.`;
    return 'Se emite un día después de terminar el evento.';
};

const SettlementDetail = ({ settlement }: { settlement: Settlement }) => {
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(false);
    const currency = settlement.currency;

    const download = async () => {
        setDownloading(true);
        setDownloadError(false);
        try {
            await organizerSettlementsService.downloadCsv(settlement.eventId);
        } catch {
            setDownloadError(true);
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="border-t border-[var(--st-border)]">
            <div className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Figure label="Evento">
                    {formatEventDate(settlement.eventStart, DATE_TIME_SHORT, 'Fecha por definir')}
                    {settlement.eventEnd && <span className="block text-xs text-[var(--st-text-muted)]">
                        Termina {formatEventDate(settlement.eventEnd, DATE_TIME_SHORT)}
                    </span>}
                </Figure>
                <Figure label="Corte">
                    {settlement.cutOffAt ? formatInstant(settlement.cutOffAt, DATE_TIME_SHORT) : 'Cuando el evento tenga fecha de finalización'}
                </Figure>
                <Figure label="Cargo pagado por">
                    {settlement.terms ? (settlement.terms.payer === 'ORGANIZER' ? 'Organizador' : 'Comprador') : '—'}
                </Figure>
                <Figure label="Tarifa">
                    {settlement.terms
                        ? <>
                            {formatPercent(settlement.terms.rate)} por boleta
                            <span className="block text-xs text-[var(--st-text-muted)]">
                                Mínimo {formatCurrency(settlement.terms.minAmount, currency)}
                                {settlement.terms.maxAmount != null ? ` · máximo ${formatCurrency(settlement.terms.maxAmount, currency)}` : ''}
                                {' · IVA '}{settlement.terms.taxMode === 'TAX_ADDED' ? 'adicional' : 'incluido'}
                            </span>
                        </>
                        : '—'}
                </Figure>
            </div>

            {settlement.lines.length === 0 ? (
                <p className="px-5 pb-5 text-sm text-[var(--st-text-muted)]">Aún no hay ventas con boletas emitidas.</p>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-sm">
                        <thead>
                            <tr className="bg-[var(--st-bg-tertiary)] text-xs uppercase text-[var(--st-text-muted)] tracking-wider">
                                <th className="p-4 font-bold">Etapa</th>
                                <th className="p-4 font-bold">Pasarela</th>
                                <th className="p-4 font-bold text-right">Boletas</th>
                                <th className="p-4 font-bold text-right">Ventas de boletas</th>
                                <th className="p-4 font-bold text-right">Cargo por servicio</th>
                            </tr>
                        </thead>
                        <tbody>
                            {settlement.lines.map((line, index) => (
                                <tr key={index} className="border-t border-[var(--st-border)]">
                                    <td className="p-4 font-medium text-[var(--st-text-primary)]">{line.stageName ?? 'Entrada'}</td>
                                    <td className="p-4 text-[var(--st-text-secondary)]">{line.provider ? PROVIDER_LABELS[line.provider] : '—'}</td>
                                    <td className="p-4 text-right text-[var(--st-text-secondary)]">{formatNumber(line.tickets)}</td>
                                    <td className="p-4 text-right text-[var(--st-text-secondary)]">{formatCurrency(line.ticketSales, currency)}</td>
                                    <td className="p-4 text-right font-bold text-[var(--st-text-primary)]">{formatCurrency(line.serviceFee, currency)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="p-5 border-t border-[var(--st-border)] grid grid-cols-2 lg:grid-cols-5 gap-4">
                <Figure label="Órdenes">{formatNumber(settlement.totals.orders)}</Figure>
                <Figure label="Ventas de boletas">{formatCurrency(settlement.totals.ticketSales, currency)}</Figure>
                <Figure label="Cargo sin IVA">{formatExact(settlement.totals.serviceFeeNet, currency)}</Figure>
                <Figure label="IVA del cargo">{formatExact(settlement.totals.serviceFeeTax, currency)}</Figure>
                <Figure label={settlement.status === 'OPEN' ? 'Cargo acumulado' : 'Valor a cobrar'} strong>
                    {formatCurrency(settlement.amountDue, currency)}
                </Figure>
            </div>

            {settlement.pending.orders > 0 && (
                <div className="st-alert st-alert-warning mx-5 mb-5">
                    <div className="st-alert-content">
                        <p className="st-alert-message">
                            {settlement.pending.orders === 1 ? '1 orden pagada aún no tiene' : `${settlement.pending.orders} órdenes pagadas aún no tienen`} boletas
                            (cargo de {formatCurrency(settlement.pending.serviceFee, currency)}). No se incluyen en el valor a cobrar; revísalas en Órdenes en revisión.
                        </p>
                    </div>
                </div>
            )}

            <div className="px-5 pb-5 flex flex-col sm:flex-row sm:items-center gap-3">
                <Button className="st-btn st-btn-primary st-btn-md w-full sm:w-auto" onClick={download} disabled={downloading}>
                    {downloading ? 'Descargando...' : 'Descargar CSV'}
                </Button>
                {downloadError && <span className="text-sm text-[var(--st-error)]">No pudimos descargar el archivo. Intenta de nuevo.</span>}
            </div>
        </div>
    );
};

const SettlementCard = ({ summary }: { summary: SettlementSummary }) => {
    const [open, setOpen] = useState(false);
    const [settlement, setSettlement] = useState<Settlement | null>(null);
    const [loadError, setLoadError] = useState(false);
    const status = STATUS[summary.status];

    const toggle = async () => {
        const next = !open;
        setOpen(next);
        if (!next || settlement) return;
        try {
            setLoadError(false);
            setSettlement(await organizerSettlementsService.getSettlement(summary.eventId));
        } catch {
            setLoadError(true);
        }
    };

    return (
        <div className="st-card-static overflow-hidden">
            <div className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <div className="flex items-center gap-3 flex-wrap mb-1">
                        <h2 className="font-display text-lg font-bold text-[var(--st-text-primary)]">{summary.eventName ?? 'Evento'}</h2>
                        <span className={`st-badge ${status.badge}`}>{status.label}</span>
                    </div>
                    <p className="text-sm text-[var(--st-text-secondary)]">
                        {formatNumber(summary.tickets)} {summary.tickets === 1 ? 'boleta' : 'boletas'} · {cutOffText(summary)}
                        {summary.pendingOrders > 0 && ` · ${summary.pendingOrders} pendientes`}
                    </p>
                </div>
                <div className="flex items-center gap-6">
                    <div className="text-right">
                        <p className="text-xs text-[var(--st-text-muted)] uppercase font-bold tracking-wider">
                            {summary.status === 'OPEN' ? 'Cargo acumulado' : 'Valor a cobrar'}
                        </p>
                        <p className="font-display text-xl font-bold text-[var(--st-text-primary)]">{formatCurrency(summary.amountDue, summary.currency)}</p>
                    </div>
                    <Button variant="secondary" className="st-btn st-btn-md whitespace-nowrap" onClick={toggle}>
                        {open ? 'Ocultar' : 'Ver detalle'}
                    </Button>
                </div>
            </div>
            {open && !settlement && !loadError && (
                <div className="p-6 flex justify-center border-t border-[var(--st-border)]"><div className="st-spinner"></div></div>
            )}
            {open && loadError && (
                <p className="p-5 border-t border-[var(--st-border)] text-sm text-[var(--st-text-secondary)]">No pudimos cargar la liquidación.</p>
            )}
            {open && settlement && <SettlementDetail settlement={settlement} />}
        </div>
    );
};

export default function OrganizerSettlements() {
    const [settlements, setSettlements] = useState<SettlementSummary[] | null>(null);
    const [loadError, setLoadError] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoadError(false);
            setSettlements(await organizerSettlementsService.getMySettlements());
        } catch {
            setLoadError(true);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const due = (settlements ?? []).filter(s => s.status === 'ISSUED').reduce((sum, s) => sum + s.amountDue, 0);
    const accruing = (settlements ?? []).filter(s => s.status === 'OPEN').reduce((sum, s) => sum + s.amountDue, 0);

    return (
        <div className="st-page py-12">
            <div className="st-container">
                <div className="mb-8">
                    <h1 className="font-display text-3xl font-bold text-[var(--st-text-primary)]">Liquidaciones</h1>
                    <p className="text-[var(--st-text-secondary)] max-w-3xl">
                        El cargo por servicio SuperTicket de cada evento. Tu pasarela te paga el valor completo de cada compra,
                        cargo incluido; un día después de terminar el evento emitimos su liquidación y te enviamos la cuenta de
                        cobro por ese valor. Un evento cancelado no genera cargo.
                    </p>
                </div>

                {settlements === null && !loadError && (
                    <div className="flex items-center justify-center min-h-[30vh]"><div className="st-spinner st-spinner-xl"></div></div>
                )}

                {settlements === null && loadError && (
                    <div className="st-card-static p-8 text-center">
                        <p className="text-[var(--st-text-secondary)] mb-4">No pudimos cargar tus liquidaciones.</p>
                        <Button className="st-btn st-btn-primary" onClick={load}>Intentar de Nuevo</Button>
                    </div>
                )}

                {settlements !== null && settlements.length === 0 && (
                    <div className="st-card-static p-10 text-center">
                        <h2 className="font-display text-xl font-bold text-[var(--st-text-primary)] mb-2">Aún no tienes liquidaciones</h2>
                        <p className="text-[var(--st-text-secondary)]">Aparecerán aquí cuando tus eventos tengan ventas.</p>
                    </div>
                )}

                {settlements !== null && settlements.length > 0 && (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                            <div className="st-card-static p-5">
                                <Figure label="Por cobrar (liquidaciones emitidas)" strong>{formatCurrency(due)}</Figure>
                            </div>
                            <div className="st-card-static p-5">
                                <Figure label="Acumulado en eventos en curso" strong>{formatCurrency(accruing)}</Figure>
                            </div>
                        </div>
                        <div className="space-y-6">
                            {settlements.map(summary => <SettlementCard key={summary.eventId} summary={summary} />)}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
